// Eskom PARCC — pure derivation of the Rule Book's domain-sourced tables.
//
// The Rule Book is a static governance reference rendered entirely from the
// domain (no fetching). These pure helpers build the table rows the page draws,
// so the mapping logic is unit-testable without React:
//
//   • `cesStatusRegisterRows` — runs every real CesStatus through the live
//     `cesMapStatusToRegister` so the page shows the *actual* status→register
//     mapping the CES engine applies, not a hand-copied table that could drift.
//   • `routeSummaries` — summarises each of the 8 procurement routes from
//     `CES_ROUTE_DEFS` (criterion count + the distinct assessment stages).
//
// Keeping these here (rather than inline in the page) means a future change to
// the CES mapping or the route definitions is caught by a test, not by eyeball.
import { CES_STATUS_OPTIONS, CES_ROUTES, type CesRoute } from '../../domain/enums';
import { cesMapStatusToRegister } from '../../domain/ces';
import { CES_ROUTE_DEFS } from '../../domain/rulebook/routes';

/** One row of the CES-status → register-level mapping table. */
export interface CesStatusRegisterRow {
  /** The reviewer-selectable CES status (the unset '' default is shown as a dash). */
  status: string;
  /** What `cesMapStatusToRegister` resolves that status to on the register. */
  register: string;
}

/**
 * The CES-status → register-level rows, derived by running each real
 * {@link CES_STATUS_OPTIONS} member through {@link cesMapStatusToRegister}. This
 * is the canonical "what does each CES status mean on the register" table; it
 * tracks the engine because it calls it.
 */
export function cesStatusRegisterRows(): CesStatusRegisterRow[] {
  return CES_STATUS_OPTIONS.map((status) => ({
    status,
    register: cesMapStatusToRegister(status),
  }));
}

/** A summary of one procurement route, for the routes overview table. */
export interface RouteSummary {
  name: CesRoute;
  /** Number of CES criteria the route evaluates. */
  criterionCount: number;
  /** The distinct assessment stages, in first-seen order. */
  stages: string[];
}

/**
 * Summarise the 8 procurement routes from {@link CES_ROUTE_DEFS}: the criterion
 * count and the distinct stage headings (de-duplicated, order-preserving) each
 * route walks through. Routes are listed in the canonical {@link CES_ROUTES}
 * order.
 */
export function routeSummaries(): RouteSummary[] {
  return CES_ROUTES.map((name) => {
    const def = CES_ROUTE_DEFS[name];
    const stages: string[] = [];
    for (const c of def.criteria) {
      if (!stages.includes(c.stage)) stages.push(c.stage);
    }
    return { name, criterionCount: def.criteria.length, stages };
  });
}
